import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { memoryComparison, type MemoryComparison } from '@/db/queries'

export default function YearAgoCard() {
  const [data, setData] = useState<MemoryComparison | null>(null)

  useEffect(() => {
    memoryComparison().then(setData)
  }, [])

  if (!data) return null

  // User has < 30 days of data — hide the card entirely so the Dashboard
  // doesn't look broken or empty. It'll appear automatically once enough
  // history exists.
  if (data.interval === null) {
    return null
  }

  // We have an interval picked but the specific past day has no data
  // (e.g. user took a few weeks off Whoop). Show a soft message.
  if (!data.past) {
    return (
      <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
        <div className="flex items-baseline justify-between mb-1">
          <div className="text-xs uppercase tracking-wider text-stone-500">{data.label}</div>
          <div className="text-xs text-stone-500">{formatDate(data.pastDay)}</div>
        </div>
        <p className="mt-2 text-sm text-stone-500">
          No data recorded that day.
        </p>
      </section>
    )
  }

  const t = data.today
  const y = data.past
  const date = formatDate(data.pastDay)

  return (
    <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-xs uppercase tracking-wider text-stone-500">{data.label}</div>
        <div className="text-xs text-stone-500">{date}</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Compare label="Recovery" today={t?.recovery} ago={y.recovery} unit="%" toneFor="recovery" />
        <Compare label="Strain" today={t?.strain} ago={y.strain} decimals={1} toneFor="strain" />
        <Compare label="HRV" today={t?.hrv} ago={y.hrv} unit=" ms" toneFor="neutral" />
        <Compare
          label="Sleep"
          today={t?.sleep_minutes}
          ago={y.sleep_minutes}
          formatter={formatMinutes}
          toneFor="sleep"
        />
      </div>
    </section>
  )
}

function Compare({
  label,
  today,
  ago,
  unit = '',
  decimals = 0,
  formatter,
  toneFor,
}: {
  label: string
  today: number | null | undefined
  ago: number | null | undefined
  unit?: string
  decimals?: number
  formatter?: (v: number) => string
  toneFor: 'recovery' | 'strain' | 'sleep' | 'neutral'
}) {
  const formatVal = (v: number | null | undefined) => {
    if (v == null) return '—'
    if (formatter) return formatter(v)
    return v.toFixed(decimals) + unit
  }
  const delta = today != null && ago != null ? today - ago : null
  const deltaColor =
    delta == null
      ? 'text-stone-400'
      : delta > 0
      ? 'text-green-600'
      : delta < 0
      ? 'text-stone-500'
      : 'text-stone-500'
  return (
    <div className="space-y-1">
      <div className="text-[11px] uppercase tracking-wider text-stone-500">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className={clsx('text-xl font-semibold tabular-nums', toneClass(toneFor, ago))}>
          {formatVal(ago)}
        </span>
      </div>
      {delta != null && (
        <div className={clsx('text-xs tabular-nums', deltaColor)}>
          {delta > 0 ? '↑' : delta < 0 ? '↓' : '·'} {formatVal(today)} today
        </div>
      )}
    </div>
  )
}

function toneClass(tone: string, v?: number | null): string {
  if (tone === 'recovery' && v != null) {
    if (v >= 67) return 'text-whoop-recovery-high'
    if (v >= 34) return 'text-whoop-recovery-mid'
    return 'text-whoop-recovery-low'
  }
  if (tone === 'strain') return 'text-whoop-strain'
  if (tone === 'sleep') return 'text-whoop-sleep'
  return 'text-stone-900'
}

function formatMinutes(m: number): string {
  const total = Math.max(0, Math.round(m))
  const h = Math.floor(total / 60)
  const min = total % 60
  if (h === 0) return `${min}m`
  if (min === 0) return `${h}h`
  return `${h}h ${min}m`
}

function formatDate(iso: string): string {
  const d = new Date(iso + 'T12:00:00')
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}
